import { useState } from 'react';
import {
  RiAddLine,
  RiCheckLine,
  RiCloseLine,
  RiDeleteBin6Line,
  RiLightbulbLine,
  RiRefreshLine,
  RiUserLine,
} from 'react-icons/ri';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useStore } from '../../state/store';
import { ASSESS_STATUS_LABEL } from '../icons';
import { cn, formatDate } from '@/lib/utils';

export function HypothesesPanel() {
  const hypotheses = useStore(state => state.hypotheses);
  const activeTargetId = useStore(state => state.activeTargetId);
  const addHypothesis = useStore(state => state.addHypothesis);
  const updateHypothesis = useStore(state => state.updateHypothesis);
  const deleteHypothesis = useStore(state => state.deleteHypothesis);
  const [statement, setStatement] = useState('');
  const [alternatives, setAlternatives] = useState('');

  const aiCount = hypotheses.filter(h => h.createdBy === 'ai').length;

  return (
    <div className="flex flex-col gap-2.5 p-3">
      <div className="flex items-center justify-between">
        <span className="label-caps">研判记录</span>
        <Badge variant="muted">
          {hypotheses.length} 条 / 系统 {aiCount}
        </Badge>
      </div>

      <div className="flex flex-col gap-2 rounded-[var(--radius)] border border-border bg-card p-2.5">
        <Textarea
          rows={2}
          placeholder="登记待验证的判断或推断…"
          value={statement}
          onChange={e => setStatement(e.target.value)}
        />
        <Input
          value={alternatives}
          placeholder="备择解释（逗号分隔，可选）"
          onChange={e => setAlternatives(e.target.value)}
        />
        <Button
          size="sm"
          disabled={!statement.trim()}
          onClick={async () => {
            await addHypothesis({
              statement: statement.trim(),
              targetId: activeTargetId,
              category: 'general',
              confidence: 0.5,
              alternatives: alternatives
                .split(/[,，\n]+/)
                .map(t => t.trim())
                .filter(Boolean),
              createdBy: 'human',
            });
            setStatement('');
            setAlternatives('');
          }}
        >
          <RiAddLine className="size-3.5" />
          登记研判
        </Button>
      </div>

      <div className="flex flex-col gap-1.5">
        {hypotheses.map(hypothesis => (
          <div
            key={hypothesis.id}
            className="rounded-[var(--radius)] border border-border bg-card p-2.5"
            style={{
              borderLeftWidth: 3,
              borderLeftColor:
                hypothesis.status === 'supported'
                  ? 'var(--color-evidence)'
                  : hypothesis.status === 'refuted'
                    ? 'var(--color-cord)'
                    : hypothesis.createdBy === 'ai'
                      ? 'var(--color-lead)'
                      : 'var(--color-assess)',
            }}
          >
            <div className="flex items-center gap-1.5">
              <Badge variant={hypothesis.createdBy === 'ai' ? 'lead' : 'muted'} className="gap-1">
                {hypothesis.createdBy === 'ai' ? <RiLightbulbLine className="size-3" /> : <RiUserLine className="size-3" />}
                {hypothesis.createdBy === 'ai' ? '系统研判' : '人工'}
              </Badge>
              <Badge
                variant={
                  hypothesis.status === 'supported' ? 'evidence' : hypothesis.status === 'refuted' ? 'cord' : 'outline'
                }
              >
                {ASSESS_STATUS_LABEL[hypothesis.status]}
              </Badge>
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                {Math.round(hypothesis.confidence * 100)}%
              </span>
            </div>
            <p className="mt-1.5 text-[11.5px] leading-relaxed">{hypothesis.statement}</p>
            {hypothesis.rationale && (
              <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{hypothesis.rationale}</p>
            )}
            {hypothesis.alternatives.length > 0 && (
              <div className="mt-1.5 flex flex-col gap-0.5">
                <span className="text-[9.5px] uppercase tracking-wider text-muted-foreground">备择解释</span>
                {hypothesis.alternatives.map((item, index) => (
                  <span key={index} className="text-[10.5px] text-muted-foreground">
                    · {item}
                  </span>
                ))}
              </div>
            )}
            {(hypothesis.supporting.length > 0 || hypothesis.contradicting.length > 0) && (
              <div className="mt-1.5 flex items-center gap-2 text-[10px]">
                <span className="text-[var(--color-evidence)]">支持 {hypothesis.supporting.length}</span>
                <span className="text-[var(--color-cord)]">反证 {hypothesis.contradicting.length}</span>
              </div>
            )}
            <div className="mt-2 flex items-center gap-1 border-t border-border pt-1.5">
              <span className="mr-auto text-[9.5px] text-muted-foreground">{formatDate(hypothesis.createdAt)}</span>
              <Button
                size="xs"
                variant={hypothesis.status === 'supported' ? 'default' : 'ghost'}
                onClick={() => updateHypothesis(hypothesis.id, { status: 'supported' })}
              >
                <RiCheckLine className="size-3" />
                支持
              </Button>
              <Button
                size="xs"
                variant={hypothesis.status === 'refuted' ? 'destructive' : 'ghost'}
                onClick={() => updateHypothesis(hypothesis.id, { status: 'refuted' })}
              >
                <RiCloseLine className="size-3" />
                排除
              </Button>
              <Button size="xs" variant="ghost" onClick={() => updateHypothesis(hypothesis.id, { status: 'open' })}>
                <RiRefreshLine className="size-3" />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                className={cn('text-muted-foreground hover:text-destructive')}
                onClick={() => deleteHypothesis(hypothesis.id)}
              >
                <RiDeleteBin6Line className="size-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {hypotheses.length === 0 && (
        <div className="rounded-[var(--radius)] border border-dashed border-border p-6 text-center text-[11.5px] text-muted-foreground">
          尚无研判记录。可在右侧执行研判推演。
        </div>
      )}
    </div>
  );
}